import { useTheme } from '@mui/material';
import createStyles from '@mui/styles/createStyles';
import makeStyles from '@mui/styles/makeStyles';
import { MutableRefObject } from 'react';
import { blockFillParent, flexFillParent, useMakeResponsiveStyles } from 'kineticUtils';

import { CardPageProps } from './types';

const useCardPageStyles = makeStyles(({ spacing, shadows, palette }) =>
  createStyles({
    card: {
      ...flexFillParent,
      margin: `0 ${spacing(2)} ${spacing(2)}`,
      boxShadow: shadows[6],
      flexDirection: 'column',
      zIndex: 2,
      flexBasis: `calc(75% - ${spacing(4)})`,
      transform: 'translate(0px)',
      backgroundColor: palette.mode === 'dark' && palette.grey['A700'],
      border: palette.mode === 'dark' ? `1px solid ${palette.grey['A800']}` : undefined,
    },
    cardHeader: {
      display: 'flex',
      padding: spacing(2),
    },
    cardContent: {
      ...blockFillParent,
    },
    cardFooter: {},
    columnsWrapper: {
      ...flexFillParent,
    },
    contentWrapper: {
      ...flexFillParent,
      flexWrap: 'wrap',
      overflow: 'hidden',
    },
    loadingBar: { position: 'absolute' },
    extrasWrapper: {
      width: 350,
      height: '100%',
      zIndex: 1,
      position: 'relative',
    },
    extrasContent: {
      display: 'flex',
      flexDirection: 'column',
      marginBottom: spacing(2),
      marginRight: spacing(2),

      '& > *': {
        flex: `1`,
        marginBottom: spacing(2),
        '&:last-of-type': {
          marginBottom: 0,
        },
      },
    },
    fullHeightExtras: {
      height: `calc(100% - ${spacing(2)})`,
    },
    responsiveMenu: {
      paddingRight: spacing(2),
      height: 400,
      overflow: 'scroll',
    },
    header: {
      borderBottom: `1px solid ${palette.grey[500]}`,
      marginBottom: spacing(4),
      position: 'sticky',
      top: 0,
      width: '100%',
      background: palette.background.default,
    },
    sidePanelDrawer: {
      padding: spacing(2),
      '@media (max-width: 1200px)': {
        padding: `0 ${spacing(2)} 0 0`,
      },
      '@media (max-width: 768px)': {
        padding: 0,
      },
    },
    sidePanelDrawerTitle: {
      maxWidth: 280,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      paddingTop: spacing(1.5),
    },
  })
);

export default useCardPageStyles;

export const useResponsiveStyles = (containerRef: MutableRefObject<HTMLElement | null>) => {
  const { spacing } = useTheme();
  return useMakeResponsiveStyles(containerRef, {
    content: {
      base: { flex: 1, flexFlow: 'column' },
      xl: { flexFlow: 'row' },
    },
    sidebar: {
      base: {
        height: '100%',
        boxSizing: 'border-box',
        paddingTop: ({ headerProps, sidebarProps }: CardPageProps) => {
          const { expanded = true } = headerProps || {};
          const { search = true } = sidebarProps || {};
          return expanded ? (search ? spacing(3) : spacing(12.5)) : 0;
        },
      },
      sm: {
        display: 'none',
      },
      md: {
        display: 'none',
      },
      lg: {
        display: 'none',
      },
    },
    menuToggleButton: {
      base: {
        marginRight: spacing(2),
        minWidth: spacing(4),
      },
      sm: {
        display: 'none',
      },
      md: {
        display: 'none',
      },
      lg: {
        display: 'none',
      },
    },
    responsiveMenuToggleButton: {
      base: {
        marginleft: spacing(2),
        minWidth: spacing(4),
      },
      xl: {
        display: 'none',
      },
    },
  });
};
